import { Component, lazy, Suspense } from "react";
import type { ComponentType, ErrorInfo, ReactNode } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

import { RequireAuth } from "@src/auth/RequireAuth";
import { AppShell } from "@src/layout/AppShell";

const RELOAD_KEY = "chunk_reload";
const RELOAD_PARAM = "chunk_reload";
const RELOAD_WINDOW_MS = 10_000;

function getErrorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  return String(error);
}

function isChunkError(error: unknown): boolean {
  const text = getErrorText(error);
  return (
    text.includes("Failed to fetch dynamically imported module") ||
    text.includes("Failed to load module script") ||
    text.includes("Importing a module script failed") ||
    text.includes("error loading dynamically imported module") ||
    text.includes("ChunkLoadError")
  );
}

function getReloadTimestampFromUrl(): number | null {
  const value = new URL(window.location.href).searchParams.get(RELOAD_PARAM);
  if (!value) return null;
  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function shouldReload(): boolean {
  const now = Date.now();
  const urlTimestamp = getReloadTimestampFromUrl();
  if (urlTimestamp !== null && now - urlTimestamp < RELOAD_WINDOW_MS) return false;

  try {
    const last = sessionStorage.getItem(RELOAD_KEY);
    if (last && now - Number(last) < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return true;
  }

  return true;
}

function reloadForFreshBuild(): void {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

function clearReloadMarker(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_PARAM)) return;
    url.searchParams.delete(RELOAD_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // Ignore storage/history failures; they should not block route rendering.
  }
}

function lazyRetry<T extends ComponentType<object>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory()
      .then((module) => {
        clearReloadMarker();
        return module;
      })
      .catch((error: unknown) => {
        if (isChunkError(error) && shouldReload()) {
          reloadForFreshBuild();
          return new Promise<never>(() => {});
        }
        throw error;
      }),
  );
}

const AIMemoryPage = lazyRetry(() =>
  import("@src/pages/AIMemory").then((m) => ({ default: m.AIMemoryPage })),
);
const AgentInboxPage = lazyRetry(() =>
  import("@src/pages/AgentInbox").then((m) => ({ default: m.AgentInboxPage })),
);
const AuditLogPage = lazyRetry(() =>
  import("@src/pages/AuditLog").then((m) => ({ default: m.AuditLogPage })),
);
const ApplicationsPage = lazyRetry(() =>
  import("@src/pages/Applications").then((m) => ({ default: m.ApplicationsPage })),
);
const AssetGenerationPage = lazyRetry(() =>
  import("@src/pages/AssetGeneration").then((m) => ({ default: m.AssetGenerationPage })),
);
const AssetsPage = lazyRetry(() =>
  import("@src/pages/Creatives").then((m) => ({ default: m.AssetsPage })),
);
const AdCreativesPage = lazyRetry(() =>
  import("@src/pages/AdCreatives").then((m) => ({ default: m.AdCreativesPage })),
);
const AdCreativeImportPage = lazyRetry(() =>
  import("@src/pages/AdCreativeImport").then((m) => ({ default: m.AdCreativeImportPage })),
);
const BrucePage = lazyRetry(() =>
  import("@src/pages/Bruce").then((m) => ({ default: m.BrucePage })),
);
const ConversionReportPage = lazyRetry(() =>
  import("@src/pages/ConversionReport").then((m) => ({ default: m.ConversionReportPage })),
);
const DashboardPage = lazyRetry(() =>
  import("@src/pages/Dashboard").then((m) => ({ default: m.DashboardPage })),
);
const ExperimentsListPage = lazyRetry(() =>
  import("@src/pages/ExperimentsList").then((m) => ({ default: m.ExperimentsListPage })),
);
const ExperimentEditPage = lazyRetry(() =>
  import("@src/pages/ExperimentEdit").then((m) => ({ default: m.ExperimentEditPage })),
);
const FunnelDetailRedirect = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/funnels/${id ?? ""}/edit`} replace />;
};
const FunnelVisualEditor = lazyRetry(() =>
  import("@src/pages/FunnelVisualEditor").then((m) => ({ default: m.FunnelVisualEditor })),
);
const InletsPage = lazyRetry(() =>
  import("@src/pages/Inlets").then((m) => ({ default: m.InletsPage })),
);
const FunnelsPage = lazyRetry(() =>
  import("@src/pages/Funnels").then((m) => ({ default: m.FunnelsPage })),
);
const OverviewPage = lazyRetry(() =>
  import("@src/pages/Overview").then((m) => ({ default: m.OverviewPage })),
);
const ExperimentResultsPage = lazyRetry(() =>
  import("@src/pages/ExperimentResults").then((m) => ({ default: m.ExperimentResultsPage })),
);
const StepAnalyticsPage = lazyRetry(() =>
  import("@src/pages/StepAnalytics").then((m) => ({ default: m.StepAnalyticsPage })),
);
const StepTemplatesPage = lazyRetry(() =>
  import("@src/pages/StepTemplates").then((m) => ({ default: m.StepTemplatesPage })),
);
const StepTemplateEditorPage = lazyRetry(() =>
  import("@src/pages/StepTemplateEditor").then((m) => ({ default: m.StepTemplateEditorPage })),
);
const AdSpendPage = lazyRetry(() =>
  import("@src/pages/AdSpend").then((m) => ({ default: m.AdSpendPage })),
);
const OptimizationPage = lazyRetry(() =>
  import("@src/pages/Optimization").then((m) => ({ default: m.OptimizationPage })),
);
const ThumbnailRenderPage = lazyRetry(() =>
  import("@src/pages/ThumbnailRender").then((m) => ({ default: m.ThumbnailRenderPage })),
);

const LazyFallback = (): React.ReactElement => (
  <div className="flex h-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-default-300 border-t-primary" />
  </div>
);

class ChunkErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly hasError: boolean }
> {
  constructor(props: { readonly children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- Surface chunk load failures for debugging.
    console.error("Chunk load failed", error, info);
    if (isChunkError(error) && shouldReload()) {
      reloadForFreshBuild();
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <p className="text-default-600">Failed to load this page.</p>
          <button
            type="button"
            className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const Lazy = ({ children }: { readonly children: React.ReactNode }): React.ReactElement => (
  <ChunkErrorBoundary>
    <Suspense fallback={<LazyFallback />}>{children}</Suspense>
  </ChunkErrorBoundary>
);

const PreviewRedirect = (): React.ReactElement => {
  const { funnelId } = useParams<{ funnelId: string }>();
  return <Navigate to={`/funnels/${funnelId ?? ""}/edit?preview=1`} replace />;
};

export const router = createBrowserRouter([
  // Public route — no auth, no AppShell. Used by the thumbnail generation
  // service (CF Browser Rendering navigates here to screenshot a step).
  {
    path: "/thumbnail-render",
    element: (
      <Lazy>
        <ThumbnailRenderPage />
      </Lazy>
    ),
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        path: "/",
        element: (
          <Lazy>
            <OverviewPage />
          </Lazy>
        ),
      },
      {
        path: "/agent-inbox",
        element: (
          <Lazy>
            <AgentInboxPage />
          </Lazy>
        ),
      },
      {
        path: "/funnels",
        element: (
          <Lazy>
            <FunnelsPage />
          </Lazy>
        ),
      },
      {
        path: "/funnels/:id",
        element: <FunnelDetailRedirect />,
      },
      {
        path: "/funnels/:funnelId/edit",
        element: (
          <Lazy>
            <FunnelVisualEditor />
          </Lazy>
        ),
      },
      {
        path: "/funnels/:funnelId/preview",
        element: <PreviewRedirect />,
      },
      {
        path: "/experiments/new",
        element: <Navigate to="/experiments?new=1" replace />,
      },
      {
        path: "/experiments/:experimentId/edit",
        element: (
          <Lazy>
            <ExperimentEditPage />
          </Lazy>
        ),
      },
      {
        path: "/experiments/:experimentId",
        element: (
          <Lazy>
            <ExperimentResultsPage />
          </Lazy>
        ),
      },
      {
        path: "/step-analytics",
        element: (
          <Lazy>
            <StepAnalyticsPage />
          </Lazy>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      {
        path: "/reports",
        element: (
          <Lazy>
            <ConversionReportPage />
          </Lazy>
        ),
      },
      {
        path: "/assets",
        element: (
          <Lazy>
            <AssetsPage />
          </Lazy>
        ),
      },
      { path: "/creatives", element: <Navigate to="/assets" replace /> },
      {
        path: "/ad-creatives",
        element: (
          <Lazy>
            <AdCreativesPage />
          </Lazy>
        ),
      },
      {
        path: "/ad-creatives/import",
        element: (
          <Lazy>
            <AdCreativeImportPage />
          </Lazy>
        ),
      },
      {
        path: "/bruce",
        element: (
          <Lazy>
            <BrucePage />
          </Lazy>
        ),
      },
      {
        path: "/generate",
        element: (
          <Lazy>
            <AssetGenerationPage />
          </Lazy>
        ),
      },
      {
        path: "/applications",
        element: (
          <Lazy>
            <ApplicationsPage />
          </Lazy>
        ),
      },
      {
        path: "/experiments",
        element: (
          <Lazy>
            <ExperimentsListPage />
          </Lazy>
        ),
      },
      {
        path: "/inlets",
        element: (
          <Lazy>
            <InletsPage />
          </Lazy>
        ),
      },
      { path: "/campaigns", element: <Navigate to="/inlets" replace /> },
      {
        path: "/step-templates",
        element: (
          <Lazy>
            <StepTemplatesPage />
          </Lazy>
        ),
      },
      {
        path: "/step-templates/new",
        element: (
          <Lazy>
            <StepTemplateEditorPage />
          </Lazy>
        ),
      },
      {
        path: "/step-templates/:id/edit",
        element: (
          <Lazy>
            <StepTemplateEditorPage />
          </Lazy>
        ),
      },
      {
        path: "/ai-memory",
        element: (
          <Lazy>
            <AIMemoryPage />
          </Lazy>
        ),
      },
      {
        path: "/ad-spend",
        element: (
          <Lazy>
            <AdSpendPage />
          </Lazy>
        ),
      },
      {
        path: "/optimization",
        element: (
          <Lazy>
            <OptimizationPage />
          </Lazy>
        ),
      },
      {
        path: "/audit-log",
        element: (
          <Lazy>
            <AuditLogPage />
          </Lazy>
        ),
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
